// ─────────────────────────────────────────────────────────────
// Éléments absents du pack Kenney, dessinés en vectoriel dans le
// même langage graphique : aplats + contour brun épais, lumière
// venant du haut-gauche. Ça couvre les habitants (le pack n'a
// aucun personnage), les lampes et les ponts entre parcelles.
// ─────────────────────────────────────────────────────────────
import { PAL } from "./tileset.js";

const stroke = (ctx, w) => {
  ctx.strokeStyle = PAL.inkDeep;
  ctx.lineWidth = w;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
};

function blob(ctx, x, y, rx, ry, fill, lw) {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  if (lw) { stroke(ctx, lw); ctx.stroke(); }
}

function roundRect(ctx, x, y, w, h, r, fill, lw) {
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
  else ctx.rect(x, y, w, h);
  ctx.fillStyle = fill;
  ctx.fill();
  if (lw) { stroke(ctx, lw); ctx.stroke(); }
}

export function groundShadow(ctx, x, y, rx, s) {
  ctx.save();
  ctx.globalAlpha = 0.28;
  ctx.fillStyle = "#4a2c1c";
  ctx.beginPath();
  ctx.ellipse(x, y, rx * s, rx * 0.45 * s, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// ── Habitant ──────────────────────────────────────────────────
// robe = [clair, ombre], coiffe optionnelle
export function drawVillager(ctx, x, y, s, look, t, facing = 1, moving = true) {
  const { robe, robeDark, hat, skin = "#e3b183", accent } = look;
  const bob = moving ? Math.sin(t * 5.5) * 1.6 * s : 0;
  const lw = Math.max(1.4, 3 * s);
  ctx.save();
  groundShadow(ctx, x, y, 13, s);
  ctx.translate(x, y + bob);
  ctx.scale(facing, 1);

  // jambes
  const swing = moving ? Math.sin(t * 5.5) * 5 * s : 0;
  stroke(ctx, lw * 1.6);
  ctx.beginPath();
  ctx.moveTo(-3 * s, -16 * s); ctx.lineTo(-3 * s + swing, -1 * s);
  ctx.moveTo(4 * s, -16 * s); ctx.lineTo(4 * s - swing, -1 * s);
  ctx.stroke();

  // robe
  ctx.beginPath();
  ctx.moveTo(-11 * s, -14 * s);
  ctx.quadraticCurveTo(-13 * s, -40 * s, 0, -42 * s);
  ctx.quadraticCurveTo(13 * s, -40 * s, 11 * s, -14 * s);
  ctx.closePath();
  ctx.fillStyle = robe; ctx.fill();
  stroke(ctx, lw); ctx.stroke();
  // ombre interne à droite
  ctx.save(); ctx.clip();
  ctx.fillStyle = robeDark;
  ctx.fillRect(2 * s, -44 * s, 14 * s, 34 * s);
  ctx.restore();
  ctx.beginPath();
  ctx.moveTo(-11 * s, -14 * s);
  ctx.quadraticCurveTo(-13 * s, -40 * s, 0, -42 * s);
  ctx.quadraticCurveTo(13 * s, -40 * s, 11 * s, -14 * s);
  ctx.closePath();
  stroke(ctx, lw); ctx.stroke();

  if (accent) {
    ctx.beginPath();
    ctx.moveTo(-10 * s, -24 * s); ctx.lineTo(10 * s, -24 * s);
    stroke(ctx, lw * 1.4); ctx.strokeStyle = accent; ctx.stroke();
  }

  // bras
  stroke(ctx, lw * 1.5);
  ctx.beginPath();
  ctx.moveTo(-9 * s, -36 * s); ctx.quadraticCurveTo(-15 * s, -28 * s, -12 * s - swing * .4, -20 * s);
  ctx.moveTo(9 * s, -36 * s); ctx.quadraticCurveTo(15 * s, -28 * s, 12 * s + swing * .4, -20 * s);
  ctx.stroke();

  // tête
  blob(ctx, 0, -50 * s, 9 * s, 9.5 * s, skin, lw);
  // yeux
  ctx.fillStyle = PAL.inkDeep;
  ctx.beginPath();
  ctx.ellipse(-3.5 * s, -51 * s, 1.3 * s, 1.8 * s, 0, 0, 7);
  ctx.ellipse(3.5 * s, -51 * s, 1.3 * s, 1.8 * s, 0, 0, 7);
  ctx.fill();
  // joues
  ctx.globalAlpha = .45; ctx.fillStyle = "#e8836a";
  ctx.beginPath();
  ctx.ellipse(-6.5 * s, -47 * s, 2.4 * s, 1.6 * s, 0, 0, 7);
  ctx.ellipse(6.5 * s, -47 * s, 2.4 * s, 1.6 * s, 0, 0, 7);
  ctx.fill();
  ctx.globalAlpha = 1;

  if (hat) {
    ctx.beginPath();
    ctx.moveTo(-13 * s, -57 * s); ctx.lineTo(13 * s, -57 * s);
    ctx.quadraticCurveTo(10 * s, -70 * s, 0, -70 * s);
    ctx.quadraticCurveTo(-10 * s, -70 * s, -13 * s, -57 * s);
    ctx.closePath();
    ctx.fillStyle = hat; ctx.fill();
    stroke(ctx, lw); ctx.stroke();
  }
  ctx.restore();
}

// ── Lampe ─────────────────────────────────────────────────────
// Retourne la position du foyer lumineux pour la passe d'éclairage.
export function drawLamp(ctx, x, y, s, t) {
  const lw = Math.max(1.4, 3 * s);
  groundShadow(ctx, x, y, 10, s);
  ctx.save();
  ctx.translate(x, y);
  // socle
  roundRect(ctx, -7 * s, -8 * s, 14 * s, 8 * s, 3 * s, PAL.sandDark, lw);
  // mât
  stroke(ctx, lw * 1.8);
  ctx.beginPath(); ctx.moveTo(0, -8 * s); ctx.lineTo(0, -46 * s); ctx.stroke();
  ctx.strokeStyle = PAL.clayDark; ctx.lineWidth = lw * 1.1;
  ctx.beginPath(); ctx.moveTo(0, -8 * s); ctx.lineTo(0, -46 * s); ctx.stroke();
  // lanterne
  ctx.beginPath();
  ctx.moveTo(-9 * s, -50 * s);
  ctx.lineTo(9 * s, -50 * s);
  ctx.lineTo(6 * s, -66 * s);
  ctx.lineTo(-6 * s, -66 * s);
  ctx.closePath();
  const flick = 0.86 + Math.sin(t * 7.3) * 0.06 + Math.sin(t * 2.1) * 0.05;
  ctx.fillStyle = PAL.lampWarm; ctx.globalAlpha = flick; ctx.fill(); ctx.globalAlpha = 1;
  stroke(ctx, lw); ctx.stroke();
  blob(ctx, 0, -58 * s, 3.2 * s, 4 * s, PAL.lampCore, 0);
  // chapeau
  ctx.beginPath();
  ctx.moveTo(-10 * s, -66 * s); ctx.lineTo(10 * s, -66 * s); ctx.lineTo(0, -76 * s);
  ctx.closePath();
  ctx.fillStyle = PAL.terraDark; ctx.fill(); stroke(ctx, lw); ctx.stroke();
  ctx.restore();
  return { lx: x, ly: y - 58 * s, intensity: flick };
}

// ── Pont entre deux parcelles ─────────────────────────────────
//
// L'ancien pont était un rectangle qu'on faisait pivoter : il ne
// respectait pas la perspective et flottait au-dessus de la scène.
// Celui-ci est construit dans l'espace des tuiles puis projeté, donc
// il partage exactement les mêmes fuyantes que le décor : tablier
// épais avec ses deux faces visibles, planches transversales,
// poteaux et garde-corps.
//
// corners : les 4 coins du tablier, DÉJÀ projetés, dans l'ordre
//           arrière-gauche, arrière-droit, avant-droit, avant-gauche.
export function drawIsoBridge(ctx, corners, S) {
  const lw = Math.max(1.2, 2.6 * S);
  const [a, b, c, d] = corners;
  const thick = 13 * S;
  const down = (p) => ({ x: p.x, y: p.y + thick });

  // ombre portée sur l'eau
  ctx.save();
  ctx.globalAlpha = 0.22;
  ctx.fillStyle = "#0d3346";
  ctx.beginPath();
  ctx.moveTo(a.x, a.y + thick * 1.5);
  [b, c, d].forEach((p) => ctx.lineTo(p.x, p.y + thick * 1.5));
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  // faces latérales (les deux arêtes tournées vers le spectateur)
  const face = (p, q, fill) => {
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(q.x, q.y);
    ctx.lineTo(down(q).x, down(q).y);
    ctx.lineTo(down(p).x, down(p).y);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    stroke(ctx, lw);
    ctx.stroke();
  };
  face(d, c, PAL.clayDark);
  face(c, b, PAL.terraDark);

  // tablier
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  [b, c, d].forEach((p) => ctx.lineTo(p.x, p.y));
  ctx.closePath();
  ctx.fillStyle = PAL.sandMid;
  ctx.fill();
  stroke(ctx, lw);
  ctx.stroke();

  // planches : on interpole le long de l'axe du pont
  const lerp = (p, q, u) => ({ x: p.x + (q.x - p.x) * u, y: p.y + (q.y - p.y) * u });
  ctx.save();
  ctx.strokeStyle = PAL.sandDark;
  ctx.lineWidth = lw * 0.8;
  const planks = 9;
  for (let i = 1; i < planks; i++) {
    const u = i / planks;
    const p0 = lerp(a, b, u), p1 = lerp(d, c, u);
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.lineTo(p1.x, p1.y);
    ctx.stroke();
  }
  ctx.restore();

  // garde-corps : main courante + poteaux, sur les deux longs côtés
  const rail = (p, q) => {
    const h = 22 * S;
    const up = (pt) => ({ x: pt.x, y: pt.y - h });
    ctx.beginPath();
    ctx.moveTo(up(p).x, up(p).y);
    ctx.lineTo(up(q).x, up(q).y);
    stroke(ctx, lw * 1.5);
    ctx.strokeStyle = PAL.clayDark;
    ctx.stroke();
    for (let i = 0; i <= 4; i++) {
      const u = i / 4;
      const base = lerp(p, q, u);
      ctx.beginPath();
      ctx.moveTo(base.x, base.y);
      ctx.lineTo(base.x, base.y - h);
      ctx.lineWidth = lw * 1.4;
      ctx.strokeStyle = PAL.inkDeep;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(base.x, base.y - 1);
      ctx.lineTo(base.x, base.y - h + 1);
      ctx.lineWidth = lw * 0.7;
      ctx.strokeStyle = PAL.terraMid;
      ctx.stroke();
    }
  };
  rail(a, b);
  rail(d, c);
}

// ── Nuage ─────────────────────────────────────────────────────
export function drawCloud(ctx, x, y, s, alpha) {
  ctx.save();
  ctx.globalAlpha = alpha;
  const puffs = [[0, 0, 46], [-40, 12, 32], [42, 14, 28], [-16, -18, 34], [22, -14, 30]];
  const grad = ctx.createLinearGradient(0, y - 40 * s, 0, y + 30 * s);
  grad.addColorStop(0, "#ffffff");
  grad.addColorStop(1, "#dfe9f2");
  ctx.fillStyle = grad;
  ctx.beginPath();
  for (const [dx, dy, r] of puffs) {
    ctx.moveTo(x + dx * s + r * s, y + dy * s);
    ctx.arc(x + dx * s, y + dy * s, r * s, 0, Math.PI * 2);
  }
  ctx.fill();
  ctx.restore();
}


// ── Animaux ───────────────────────────────────────────────────
// Quadrupèdes vectoriels, même langage graphique que les habitants :
// aplats, contour brun épais, lumière en haut à gauche.
//   look = { body, dark, belly, horn, tail, hump, height, length }
export function drawAnimal(ctx, x, y, s, look, t, facing = 1, moving = true) {
  const {
    body = "#d9c9a8", dark = "#a89372", belly = null,
    horn = null, ears = "tall", hump = 0, height = 1, length = 1, mane = null,
  } = look;
  const lw = Math.max(1.3, 2.8 * s);
  const bob = moving ? Math.sin(t * 7) * 1.2 * s : Math.sin(t * 1.4) * 0.5 * s;
  const L = 20 * length * s, Hh = 13 * height * s;

  ctx.save();
  groundShadow(ctx, x, y, 12 * length, s);
  ctx.translate(x, y + bob);
  ctx.scale(facing, 1);

  // pattes
  const swing = moving ? Math.sin(t * 7) * 3.5 * s : 0;
  stroke(ctx, lw * 1.5);
  ctx.beginPath();
  ctx.moveTo(-L * 0.34, -Hh); ctx.lineTo(-L * 0.34 + swing, 0);
  ctx.moveTo(-L * 0.1, -Hh); ctx.lineTo(-L * 0.1 - swing, 0);
  ctx.moveTo(L * 0.16, -Hh); ctx.lineTo(L * 0.16 - swing, 0);
  ctx.moveTo(L * 0.36, -Hh); ctx.lineTo(L * 0.36 + swing, 0);
  ctx.stroke();

  // corps
  ctx.beginPath();
  ctx.ellipse(0, -Hh - 6 * s, L * 0.52, 8.5 * s * height, 0, 0, Math.PI * 2);
  ctx.fillStyle = body; ctx.fill();
  stroke(ctx, lw); ctx.stroke();
  if (belly) {
    ctx.save(); ctx.clip();
    ctx.fillStyle = belly;
    ctx.fillRect(-L, -Hh - 3 * s, L * 2, 10 * s);
    ctx.restore();
  }
  if (hump) {
    ctx.beginPath();
    ctx.ellipse(-2 * s, -Hh - 13 * s, 7 * s, 6 * s, 0, 0, Math.PI * 2);
    ctx.fillStyle = body; ctx.fill(); stroke(ctx, lw); ctx.stroke();
  }

  // queue
  stroke(ctx, lw * 1.2);
  ctx.beginPath();
  ctx.moveTo(-L * 0.5, -Hh - 8 * s);
  ctx.quadraticCurveTo(-L * 0.68, -Hh - 12 * s + Math.sin(t * 3) * 2 * s, -L * 0.62, -Hh - 2 * s);
  ctx.stroke();

  // cou + tête
  const hx = L * 0.52, hy = -Hh - (12 + 6 * height) * s;
  stroke(ctx, lw * 2.4);
  ctx.beginPath();
  ctx.moveTo(L * 0.36, -Hh - 8 * s); ctx.lineTo(hx, hy + 4 * s);
  ctx.strokeStyle = body; ctx.lineWidth = lw * 2; ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(hx, hy, 7 * s, 5.6 * s, 0.25, 0, Math.PI * 2);
  ctx.fillStyle = body; ctx.fill(); stroke(ctx, lw); ctx.stroke();

  if (mane) {
    ctx.beginPath();
    ctx.ellipse(hx - 5 * s, hy + 1 * s, 7 * s, 7 * s, 0, 0, Math.PI * 2);
    ctx.fillStyle = mane; ctx.fill(); stroke(ctx, lw); ctx.stroke();
  }

  // oreilles
  ctx.fillStyle = dark;
  if (ears === "tall") {
    ctx.beginPath();
    ctx.ellipse(hx - 2 * s, hy - 7 * s, 2 * s, 5 * s, -0.2, 0, Math.PI * 2);
    ctx.ellipse(hx + 2 * s, hy - 7 * s, 2 * s, 5 * s, 0.2, 0, Math.PI * 2);
    ctx.fill();
  } else if (ears === "pointy") {
    ctx.beginPath();
    ctx.moveTo(hx - 5 * s, hy - 3 * s); ctx.lineTo(hx - 3 * s, hy - 9 * s); ctx.lineTo(hx - 1 * s, hy - 3 * s);
    ctx.moveTo(hx + 1 * s, hy - 3 * s); ctx.lineTo(hx + 3 * s, hy - 9 * s); ctx.lineTo(hx + 5 * s, hy - 3 * s);
    ctx.fill();
  }
  if (horn) {
    ctx.strokeStyle = horn; ctx.lineWidth = lw * 1.4; ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(hx - 2 * s, hy - 5 * s);
    ctx.quadraticCurveTo(hx - 6 * s, hy - 12 * s, hx - 1 * s, hy - 13 * s);
    ctx.stroke();
  }

  // œil
  ctx.fillStyle = PAL.inkDeep;
  ctx.beginPath(); ctx.ellipse(hx + 2.5 * s, hy - 0.5 * s, 1.3 * s, 1.7 * s, 0, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}
